import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Col, Button, ProgressBar } from 'react-bootstrap';

import TruncatedTextTooltip from '@/components/common/TruncatedTextTooltip';
import search from '@/assets/img/icons/svg/search.svg';
import '../ChatPill.css';
import './ChatSidebar.css';

const SUGGESTED_QUESTIONS = [
    'What are the biggest water challenges in Colorado?',
    'How is climate change affecting our communities?',
    'What renewable energy options are available in Colorado?',
    'Tell me about sustainable housing initiatives',
];

function formatTimeAgo(date) {
    if (!date) return '';
    const parsed = new Date(date);
    if (isNaN(parsed.getTime()) || parsed.getTime() === 0) return '';

    const now = new Date();
    const diff = now - parsed;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return parsed.toLocaleDateString();
}

function groupChatsByTime(chatList) {
    const now = new Date();
    const todayStart = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate()
    );
    const yesterdayStart = new Date(todayStart);
    yesterdayStart.setDate(yesterdayStart.getDate() - 1);
    const lastWeekStart = new Date(todayStart);
    lastWeekStart.setDate(lastWeekStart.getDate() - 7);

    return chatList.reduce(
        (groups, c) => {
            const d = new Date(c.lastActivity || 0);
            if (d >= todayStart) groups.today.push(c);
            else if (d >= yesterdayStart) groups.yesterday.push(c);
            else if (d >= lastWeekStart) groups.lastWeek.push(c);
            else groups.older.push(c);
            return groups;
        },
        { today: [], yesterday: [], lastWeek: [], older: [] }
    );
}

ChatSidebar.propTypes = {
    isMobileSidebarOpen: PropTypes.bool.isRequired,
    isSidebarCollapsed: PropTypes.bool.isRequired,
    selectedChatId: PropTypes.string,
    searchTerm: PropTypes.string.isRequired,
    filteredChats: PropTypes.array.isRequired,
    usageStats: PropTypes.object,
    user: PropTypes.object,
    onChatSelect: PropTypes.func.isRequired,
    onDeleteChat: PropTypes.func.isRequired,
    onSearchChange: PropTypes.func.isRequired,
    onMobileClose: PropTypes.func.isRequired,
    onNewChat: PropTypes.func.isRequired,
    onToggleCollapse: PropTypes.func.isRequired,
    onSuggestedQuestion: PropTypes.func.isRequired,
};

export default function ChatSidebar({
    isMobileSidebarOpen,
    isSidebarCollapsed,
    selectedChatId,
    searchTerm,
    filteredChats,
    usageStats,
    user,
    onChatSelect,
    onDeleteChat,
    onSearchChange,
    onMobileClose,
    onNewChat,
    onToggleCollapse,
    onSuggestedQuestion,
}) {
    const searchInputRef = useRef(null);
    const [shouldFocusSearch, setShouldFocusSearch] = useState(false);

    // Focus search input after sidebar expands
    useEffect(() => {
        if (!isSidebarCollapsed && shouldFocusSearch) {
            searchInputRef.current?.focus();
            setShouldFocusSearch(false);
        }
    }, [isSidebarCollapsed, shouldFocusSearch]);

    const groups = groupChatsByTime(filteredChats);
    const timeGroups = [
        { label: 'Today', items: groups.today },
        { label: 'Yesterday', items: groups.yesterday },
        { label: 'Previous 7 Days', items: groups.lastWeek },
        { label: 'Older', items: groups.older },
    ];

    return (
        <Col
            xs={12}
            lg={4}
            xl={3}
            className={`sidebar-col d-flex flex-column ${isMobileSidebarOpen ? 'mobile-open' : ''} ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}
        >
            {isSidebarCollapsed && !isMobileSidebarOpen ? (
                /* Collapsed icon rail */
                <div className="sidebar-icon-rail d-flex flex-column align-items-center py-2">
                    <button
                        type="button"
                        className="sidebar-icon-btn"
                        onClick={onToggleCollapse}
                        aria-label="Expand sidebar"
                    >
                        <i
                            className="bi bi-layout-sidebar fs-5"
                            aria-hidden="true"
                        />
                    </button>
                    <button
                        type="button"
                        className="sidebar-icon-btn mt-1"
                        onClick={onNewChat}
                        aria-label="New chat"
                    >
                        <i
                            className="bi bi-pencil-square fs-5"
                            aria-hidden="true"
                        />
                    </button>
                    <button
                        type="button"
                        className="sidebar-icon-btn mt-1"
                        onClick={() => {
                            setShouldFocusSearch(true);
                            onToggleCollapse();
                        }}
                        aria-label="Search chats"
                    >
                        <i className="bi bi-search fs-5" aria-hidden="true" />
                    </button>
                    <div className="flex-grow-1" />
                    <div className="sidebar-profile-avatar d-flex align-items-center justify-content-center bg-warning text-primary flex-shrink-0 mb-1">
                        {user?.displayName?.[0]?.toUpperCase() ||
                            user?.email?.[0]?.toUpperCase() ||
                            'U'}
                    </div>
                </div>
            ) : (
                <div className="sidebar-card d-flex flex-column flex-grow-1 min-height-0">
                    {/* Sidebar header — hamburger left, new chat right */}
                    <div className="sidebar-header d-flex align-items-center justify-content-between px-2 pt-2 pb-1 flex-shrink-0">
                        {/* Left: collapse (desktop) or close (mobile) */}
                        <button
                            type="button"
                            className="sidebar-icon-btn d-none d-lg-flex"
                            onClick={onToggleCollapse}
                            aria-label="Collapse sidebar"
                        >
                            <i
                                className="bi bi-layout-sidebar fs-5"
                                aria-hidden="true"
                            />
                        </button>
                        <button
                            type="button"
                            className="sidebar-icon-btn d-lg-none"
                            onClick={onMobileClose}
                            aria-label="Close sidebar"
                        >
                            <i className="bi bi-x fs-5" aria-hidden="true" />
                        </button>
                        {/* Right: new chat */}
                        <button
                            type="button"
                            className="sidebar-icon-btn"
                            onClick={() => {
                                onNewChat();
                                if (isMobileSidebarOpen) onMobileClose();
                            }}
                            aria-label="New chat"
                        >
                            <i
                                className="bi bi-pencil-square fs-5"
                                aria-hidden="true"
                            />
                        </button>
                    </div>

                    {/* Search */}
                    <div className="px-2 pt-2 pb-2 flex-shrink-0">
                        <div className="sidebar-search-wrapper d-flex align-items-center gap-2">
                            <img src={search} alt="" className="icon" />
                            <input
                                type="text"
                                placeholder="Search chats"
                                value={searchTerm}
                                onChange={(e) => onSearchChange(e.target.value)}
                                ref={searchInputRef}
                                className="sidebar-search-input w-100"
                                aria-label="Search chats"
                            />
                        </div>
                    </div>

                    {/* Chat List - time grouped */}
                    <div className="chat-list-container py-1 flex-grow-1">
                        {filteredChats.length === 0 && searchTerm && (
                            <div className="text-center sidebar-muted fst-italic p-3 small">
                                No chats found
                            </div>
                        )}
                        {filteredChats.length === 0 && !searchTerm && (
                            <div className="text-center sidebar-muted fst-italic p-3 small">
                                Start a new conversation!
                            </div>
                        )}
                        {timeGroups.map(({ label, items }) =>
                            items.length > 0 ? (
                                <div key={label}>
                                    <div className="sidebar-section-label fw-bold text-uppercase">
                                        {label}
                                    </div>
                                    {items.map((chatItem) => (
                                        <div
                                            key={chatItem.id}
                                            className={`sidebar-chat-item d-flex align-items-center position-relative ${selectedChatId === chatItem.id ? 'active' : ''}`}
                                        >
                                            <button
                                                type="button"
                                                className="sidebar-chat-select-btn flex-grow-1 text-start min-width-0"
                                                onClick={() =>
                                                    onChatSelect(chatItem.id)
                                                }
                                                aria-current={
                                                    selectedChatId ===
                                                    chatItem.id
                                                }
                                            >
                                                <div className="chat-title fw-normal small mb-0">
                                                    {chatItem.title ||
                                                        'Untitled Chat'}
                                                </div>
                                                <div className="sidebar-chat-meta">
                                                    {formatTimeAgo(
                                                        chatItem.lastActivity
                                                    )}
                                                    {formatTimeAgo(
                                                        chatItem.lastActivity
                                                    ) &&
                                                        chatItem.messageCount >
                                                            0 &&
                                                        ' · '}
                                                    {chatItem.messageCount >
                                                        0 &&
                                                        `${chatItem.messageCount} msg${chatItem.messageCount !== 1 ? 's' : ''}`}
                                                </div>
                                            </button>
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="chat-delete-btn p-0 flex-shrink-0"
                                                onClick={(e) =>
                                                    onDeleteChat(chatItem.id, e)
                                                }
                                                aria-label={`Delete chat: ${chatItem.title || 'Untitled Chat'}`}
                                            >
                                                <i
                                                    className="bi bi-x"
                                                    aria-hidden="true"
                                                />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            ) : null
                        )}
                    </div>

                    {/* Suggested Questions */}
                    <div className="sidebar-bottom-section px-2 py-1 flex-shrink-0">
                        <div className="sidebar-section-label fw-bold text-uppercase">
                            Suggested
                        </div>
                        <div className="d-flex flex-column sidebar-suggested-list">
                            {SUGGESTED_QUESTIONS.map((question) => (
                                <button
                                    key={question}
                                    type="button"
                                    className="sidebar-suggested-btn text-start"
                                    onClick={() =>
                                        onSuggestedQuestion(question)
                                    }
                                >
                                    {question}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Usage Stats */}
                    {usageStats && (
                        <div className="sidebar-bottom-section px-3 py-2 flex-shrink-0">
                            <div className="sidebar-section-label ps-0">
                                Daily Usage
                            </div>
                            {/* Nested child ProgressBar so aria-label lands on
                                the inner role="progressbar" element (the outer
                                .progress wrapper has no role and may not carry
                                aria-label). Gives the bar an accessible name. */}
                            <ProgressBar className="usage-bar mb-1">
                                <ProgressBar
                                    aria-label="Daily message usage"
                                    now={
                                        usageStats.dailyLimit > 0
                                            ? (usageStats.dailyMessages /
                                                  usageStats.dailyLimit) *
                                              100
                                            : 0
                                    }
                                    variant={
                                        usageStats.dailyMessages >=
                                        usageStats.dailyLimit * 0.8
                                            ? 'warning'
                                            : 'primary'
                                    }
                                />
                            </ProgressBar>
                            <span className="sidebar-usage-text">
                                {usageStats.dailyMessages} /{' '}
                                {usageStats.dailyLimit > 0
                                    ? usageStats.dailyLimit
                                    : '\u221E'}{' '}
                                messages today
                            </span>
                        </div>
                    )}

                    {/* User Profile */}
                    <div className="sidebar-profile d-flex align-items-center gap-2 flex-shrink-0">
                        <div className="sidebar-profile-avatar d-flex align-items-center justify-content-center bg-warning text-primary flex-shrink-0">
                            {user?.displayName?.[0]?.toUpperCase() ||
                                user?.email?.[0]?.toUpperCase() ||
                                'U'}
                        </div>
                        <TruncatedTextTooltip
                            text={
                                user?.displayName ||
                                user?.email?.split('@')[0] ||
                                'User'
                            }
                            className="sidebar-profile-name"
                        />
                    </div>
                </div>
            )}
        </Col>
    );
}
